/**
 * 料理名の変更（品目詳細）。
 *
 * 書き込みは Callable(renameDish)を通す。ルール上はスタッフが品目文書を直接
 * 更新できるが、**台帳(audit_logs)への追記と同時に行う必要がある**ため
 * （素材の差し替えと同じ理由。クライアントは台帳に書けない）。
 *
 * 生成済みのマニュアル本文の見出しは変わらない。見出しは analysis.json の
 * dish_name（生成時の名前）から組版されるので、次に生成・再組版したときに
 * 追いつく。黙っていると「変えたのにマニュアルが古い名前のまま」に見えるので、
 * その場で伝える。
 */
import { useState, type FormEvent } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useI18n } from "../lib/i18n";
import type { DishDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const renameDish = httpsCallable<
  { dishId: string; name: string; nameJa: string | null },
  { ok: boolean }
>(functions, "renameDish");

export function RenameDish({
  dishId,
  dish,
  onClose,
}: {
  dishId: string;
  dish: DishDoc;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(dish.name);
  const [nameJa, setNameJa] = useState(dish.nameJa ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unchanged =
    name.trim() === dish.name && (nameJa.trim() || null) === (dish.nameJa ?? null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || unchanged) return;
    setBusy(true);
    setError(null);
    try {
      await renameDish({ dishId, name: name.trim(), nameJa: nameJa.trim() || null });
      // 品目文書は onSnapshot で流れてくるので、ここでは閉じるだけ
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card rename" onSubmit={onSubmit}>
      <h2>{t("料理名を変更")}</h2>

      <label htmlFor="rename-name">{t("料理名 *")}</label>
      <input
        id="rename-name"
        required
        maxLength={80}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <label htmlFor="rename-nameJa">{t("料理名の読み")}</label>
      <input
        id="rename-nameJa"
        maxLength={80}
        value={nameJa}
        onChange={(e) => setNameJa(e.target.value)}
        placeholder={t("空欄にすると読みを消します")}
      />

      <p className="muted small">
        {t("変更は一覧・履歴にすぐ反映されます。マニュアル本文の見出しは、次にマニュアルを生成または再組版したときに新しい名前になります。")}
      </p>
      {error && <p className="error">{error}</p>}

      <div className="rename-actions">
        <button type="submit" disabled={busy || unchanged || !name.trim()}>
          {busy ? t("保存中…") : t("保存する")}
        </button>{" "}
        <button type="button" className="link" onClick={onClose} disabled={busy}>
          {t("やめる")}
        </button>
      </div>
    </form>
  );
}
